import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';
import { Calendar, ArrowLeft, Tag, Phone, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedServices, setRelatedServices] = useState([]);
  const [otherPosts, setOtherPosts] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const loadPost = async () => {
      try {
        const res = await axios.get(API_BASE + '/api/blog/posts/' + slug);
        setPost(res.data);
        document.title = (res.data.meta_title || res.data.title) + ' | Island Coastal Electric';
        const setMeta = (name, content, property) => {
          const attr = property ? 'property' : 'name';
          let el = document.querySelector('meta[' + attr + '="' + name + '"]');
          if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
          el.setAttribute('content', content);
        };
        setMeta('description', res.data.meta_description || res.data.excerpt);
        setMeta('og:title', res.data.title, true);
        setMeta('og:description', res.data.meta_description || res.data.excerpt, true);
        setMeta('og:url', 'https://islandcoastalelectric.ca/blog/' + slug, true);
        setMeta('og:type', 'article', true);

        // Load services for "Related Services" section
        try {
          const svcRes = await axios.get(API_BASE + '/api/services');
          const services = svcRes.data;
          const content = (res.data.content + ' ' + res.data.title + ' ' + (res.data.tags || []).join(' ')).toLowerCase();
          // Match services by keyword relevance
          const SERVICE_KEYWORDS = {
            'ev-charger-installation': ['ev', 'charger', 'charging', 'electric vehicle', 'tesla', 'level 2'],
            'panel-replacement': ['panel', 'upgrade', 'breaker', 'fuse', 'amperage', '200a', '100a', 'federal pacific'],
            'generator-installation': ['generator', 'generac', 'backup power', 'outage', 'storm', 'transfer switch'],
            'lighting-installation': ['lighting', 'lights', 'pot light', 'landscape', 'led', 'dimmer', 'lutron'],
            'electrical-planning-reports': ['epr', 'strata', 'planning report', 'form b'],
            'bc-hydro-power-smart': ['bc hydro', 'rebate', 'solar', 'power factor', 'energy'],
            'residential-electrical': ['residential', 'home', 'wiring', 'renovation', 'kitchen', 'bathroom', 'knob and tube'],
            'commercial-electrical': ['commercial', 'tenant', 'warehouse', 'industrial', 'business'],
          };
          const scored = services.map(s => {
            const keywords = SERVICE_KEYWORDS[s.slug] || [];
            const score = keywords.reduce((acc, kw) => acc + (content.includes(kw) ? 1 : 0), 0);
            return { ...s, score };
          }).filter(s => s.score > 0).sort((a, b) => b.score - a.score).slice(0, 3);
          setRelatedServices(scored.length > 0 ? scored : services.slice(0, 3));
        } catch (e) { console.warn('Related services load skipped:', e.message); }

        // Load other blog posts for "More Articles"
        try {
          const allPosts = await axios.get(API_BASE + '/api/blog/posts');
          setOtherPosts(allPosts.data.filter(p => p.slug !== slug).slice(0, 2));
        } catch (e) { console.warn('Other posts load skipped:', e.message); }
      } catch (err) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    loadPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-dark pt-32 flex items-center justify-center">
        <div className="text-white/50">Loading article...</div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-primary-dark pt-32 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-electric-teal hover:underline">Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-dark pt-28 lg:pt-32" data-testid="blog-post-page">
      {/* Schema.org Article */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": post.title,
        "description": post.meta_description || post.excerpt,
        "author": { "@type": "Organization", "name": "Island Coastal Electric" },
        "publisher": { "@type": "Organization", "name": "Island Coastal Electric", "url": "https://islandcoastalelectric.ca" },
        "datePublished": post.published_at || post.created_at,
        "dateModified": post.published_at || post.created_at,
        "mainEntityOfPage": "https://islandcoastalelectric.ca/blog/" + slug
      }) }} />

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back link */}
        <Link to="/blog" className="inline-flex items-center gap-2 text-electric-teal hover:text-white transition-colors mb-8 text-sm" data-testid="back-to-blog">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>

        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="text-electric-teal text-sm font-semibold uppercase tracking-wider">{post.category}</span>
            <span className="text-white/30">|</span>
            <span className="text-white/40 text-sm flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(post.published_at || post.created_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4" data-testid="blog-post-title">
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="text-white/60 text-lg">{post.excerpt}</p>
          )}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {post.tags.map(tag => (
                <span key={tag} className="text-xs px-3 py-1 bg-white/5 text-white/50 rounded-full flex items-center gap-1">
                  <Tag className="w-3 h-3" /> {tag}
                </span>
              ))}
            </div>
          )}
        </motion.header>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="prose prose-invert prose-teal max-w-none
            prose-headings:text-white prose-headings:font-bold
            prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
            prose-p:text-white/70 prose-p:leading-relaxed prose-p:mb-4
            prose-li:text-white/70
            prose-strong:text-white
            prose-a:text-electric-teal prose-a:no-underline hover:prose-a:underline
            prose-ul:my-4 prose-ol:my-4"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
          data-testid="blog-post-content"
        />

        {/* Related Services */}
        {relatedServices.length > 0 && (
          <div className="mt-12 pt-8 border-t border-white/10" data-testid="related-services">
            <h3 className="text-white font-bold text-lg mb-4">Related Services</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              {relatedServices.map(svc => (
                <Link key={svc.slug} to={`/services/${svc.slug}`}
                  className="bg-secondary-dark rounded-lg p-4 border border-white/5 hover:border-electric-teal/20 transition-colors group"
                  data-testid={`related-${svc.slug}`}
                >
                  <h4 className="text-white font-semibold text-sm group-hover:text-electric-teal transition-colors">{svc.title}</h4>
                  <p className="text-white/40 text-xs mt-1 line-clamp-2">{svc.short_description}</p>
                  <span className="inline-flex items-center gap-1 text-electric-teal text-xs font-semibold mt-3">
                    Learn more <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Author / CTA */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="bg-secondary-dark rounded-lg p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start sm:items-center">
            <div className="flex-1">
              <p className="text-white/40 text-sm uppercase tracking-wider mb-1">Written by</p>
              <p className="text-white font-bold text-lg">{post.author}</p>
              <p className="text-white/50 text-sm mt-1">BC Hydro Alliance Member — serving all of Vancouver Island</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-electric-teal text-primary-dark font-bold">
                <Link to="/book-estimate">Get Free Estimate</Link>
              </Button>
              <Button asChild variant="outline" className="border-white/20 text-white">
                <a href="tel:778-960-2377"><Phone className="w-4 h-4 mr-2" /> 778-960-2377</a>
              </Button>
            </div>
          </div>
        </div>

        {/* More Articles */}
        {otherPosts.length > 0 && (
          <div className="mt-10" data-testid="more-articles">
            <h3 className="text-white font-bold text-lg mb-4">More Articles</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {otherPosts.map(p => (
                <Link key={p.slug} to={`/blog/${p.slug}`}
                  className="bg-secondary-dark rounded-lg p-5 border border-white/5 hover:border-electric-teal/20 transition-colors group"
                  data-testid={`more-post-${p.slug}`}
                >
                  <span className="text-electric-teal text-xs font-semibold uppercase">{p.category}</span>
                  <h4 className="text-white font-semibold mt-1 group-hover:text-electric-teal transition-colors">{p.title}</h4>
                  <p className="text-white/40 text-xs mt-2 line-clamp-2">{p.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
};

export default BlogPostPage;
